import { useEffect, useState } from "react";

/**
 * How long to wait after a keystroke before asking the server.
 *
 * These listings span every organisation, so each request is the expensive kind.
 * A quarter of a second is long enough that typing a name is one request rather
 * than eight, and short enough that the pause is not read as the page having
 * stopped.
 */
export const FILTER_SETTLE_MS = 250;

/**
 * The filter and page position a directory listing is showing.
 *
 * The filter is held twice on purpose: `input` follows every keystroke so the
 * field stays responsive, and `query` lags it so the request does not. Returning
 * `input` as the field's value is what keeps it a controlled input — binding the
 * field to the debounced value instead would make characters appear a beat after
 * they were typed.
 *
 * Changing the filter resets the offset. Without that, filtering while on page
 * four asks for rows four pages into a result set that may now have one, and the
 * listing renders empty for what looks like no reason.
 */
export const useDirectoryQuery = () => {
  const [input, setInput] = useState("");
  const [query, setQuery] = useState("");
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    // The effect also runs when nobody has typed, so this stands in for "was
    // there a keystroke?": without it a mount schedules a settle that throws
    // away a page position taken before it fires.
    if (input === query) {
      return;
    }

    const timer = setTimeout(() => {
      setQuery(input);
      setOffset(0);
    }, FILTER_SETTLE_MS);

    return () => {
      clearTimeout(timer);
    };
  }, [input, query]);

  return { query, offset, setQuery: setInput, setOffset, input };
};
